import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useDrop } from 'react-dnd';
import TreeNode, { ITreeNodeProps } from './TreeNode';
import { DraggedItem } from '../../interfaces/DnDInterfaces';
import Input from '../../classes/Input';
import { DndItemTypes, SortOrders } from '../../globals';
import MathUtils from '../../classes/utilities/MathUtils';
import useForceUpdate from '../reactUtils/useForceUpdate';
import { DecoratorMap } from '../../classes/DecoratorMap';
import { KeyCodes, ModifierKeyCodes } from '../../interfaces/Input';

interface IListViewProps
{
	children?: any;
	selectedItems?: Set<any>;
	expandedItems?: Set<any>;
	expandItemsOnce?: Set<any>;
	expandSelected?: boolean;
	startExpanded?: boolean;
	autoScroll?: boolean;
	multiSelectEnabled?: boolean;
	selectionChangedCallback?: (selected: Set<any>, deselected: Set<any>) => void;
	itemDroppedCallback?: (droppedItems: Set<any>, dropTarget: any) => void;
	itemDroppedBeforeCallback?: (droppedItems: Set<any>, dropTarget: any) => void;
	itemDroppedAfterCallback?: (droppedItems: Set<any>, dropTarget: any) => void;
	itemDeleteCallback?: (deletedItems: Set<any>) => void;
	blockRename?: boolean;
	readOnly?: boolean;
	topScrollMargin?: number;
	bottomScrollMargin?: number;
	dndDefaults?: any;
	parentDecoratorsMapID?: string;
	style?: React.CSSProperties;
	onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
	onContextMenu?: (e: React.MouseEvent<HTMLDivElement>) => void;
	sortOrder?: SortOrders;
	autoExpand?: boolean;
	autoRename?: boolean;
	contextAction?: (props, e) => void;
	className?: string;
	useAltColor?: boolean;
	doubleClickCallback?: (node) => void;
}

export default forwardRef(function ListView(props: IListViewProps, ref)
{
	// state
	const forceUpdate = useForceUpdate();
	const rootRef = useRef<HTMLDivElement>(null);
	const lastSelectedIndex = useRef(-1);
	const expandedItems = useRef(new Set());
	const lastShiftSelectedRange = useRef(new Set());
	const indexToProps = useRef(new Map());
	const nodeIdToProps = useRef(new Map());
	const idToIndex = useRef(new Map());
	const idToTreeNode = useRef(new Map());

	const listIndex = useRef({
		index: 0,
	});

	const autoScroll = props.autoScroll !== undefined ? props.autoScroll : true;


	const dndSettings = Object.assign({
		dragEnabled: false,
		dropEnabled: false,
		dragType: DndItemTypes.GENERIC_TREE_ITEM,
		dropDefinitions: [
			{
				type: DndItemTypes.GENERIC_TREE_ITEM,
				callback: onListItemDroppedInternal,
			}],
	}, props.dndDefaults);

	const [{ hovered, canDrop }, drop] = useDrop<DraggedItem, void, { hovered: boolean, canDrop: boolean }>({
		accept: dndSettings.dropDefinitions.map(definition => definition.type),
		collect: monitor => ({
			hovered: monitor.isOver({ shallow: true }),
			canDrop: monitor.canDrop()
		}),
		drop: (item: DraggedItem, monitor) =>
		{			
			//make sure the drop event wasn't already handled
			if (!monitor.didDrop())
			{
				item.type = monitor.getItemType() as string; // inject the type into the item for new reactDND version
				manageItemDrop(item);
			}
		}
	});

	useImperativeHandle(ref, () =>
	{
		return {
			activateRename: (nodeId) =>
			{
				if (props.blockRename) return;
				console.log("Activating rename for node id " + nodeId)
				const renamingItem = getRenamingItem(nodeId);
				if (renamingItem)
				{
					if (renamingItem.activateRename)
					{
						renamingItem.activateRename();
					}
				}
			}, //activateRename
			htmlElementRef: rootRef.current,
		};
	});

	useEffect(() =>
	{
		rebuildIndexToProps();
	});

	function manageItemDrop(item, dropTarget = null, bBeforeItem = undefined)
	{
		const dropCallbackMap = new Map();

		function getCallbackForType(type)
		{
			let callbackFunc = null;
			for (const definition of dndSettings.dropDefinitions)
			{
				if (definition.type === type)
				{
					callbackFunc = definition.callback;
					break;
				}
			}

			return callbackFunc ?? onListItemDroppedInternal;
		}

		for (const dropped of item.selected)
		{
			const callback = getCallbackForType(item.type);

			if (!dropCallbackMap.has(callback))
				dropCallbackMap.set(callback, []);

			dropCallbackMap.get(callback).push(dropped.props);
		}

		dropCallbackMap.forEach((value, key) => { key(value, dropTarget, bBeforeItem) });
	}

	// initialization
	const isMultiSelectEnabled = props.multiSelectEnabled ? props.multiSelectEnabled : false;
	const selectedNodeIds = props.selectedItems ? props.selectedItems : new Set();

	listIndex.current.index = 0;

	const _decorators = decorateProps();
	const sortOrder = props.sortOrder || SortOrders.None;
	const sortDirMultiplier = (sortOrder === SortOrders.Ascending ? 1 : -1);

	if (props.parentDecoratorsMapID)
	{
		//store _decorators in the map under this ID
		DecoratorMap.setMapping(props.parentDecoratorsMapID, _decorators);
	}

	buildNodeMap();

	if (props.expandSelected)
		expandSelectedItems();

	useEffect(() =>
	{
		if (props.selectedItems)
		{

			indexToProps.current.forEach((value, key) =>
			{
				if (value.nodeId === getLastSetValue(props.selectedItems))
				{
					lastSelectedIndex.current = key;
				}
			});
		}

		if (!props.expandSelected) return;
		expandSelectedItems();

	}, [props.selectedItems]);

	const [hasExpanded, setHasExpanded] = useState(false);
	useEffect(() =>
	{
		if(!hasExpanded && props.startExpanded)
		{
			for(const child of getChildrenArray(props.children))
			{
				if (React.isValidElement(child))
				{
					// TODO: fix type
					const nodeToExpand = (child.props as any)?.nodeId;
					if(nodeToExpand)
					{
						onToggleExpandedStateCallback(nodeToExpand, true, true);
					}
				}
			}
			forceUpdate();
		}

		if (!hasExpanded && props.expandItemsOnce !== undefined && props.expandItemsOnce.forEach)
		{
			props.expandItemsOnce.forEach(item =>
			{
				onToggleExpandedStateCallback(item, true, false)
			});
			forceUpdate();
		}

		if (props.expandedItems !== undefined && props.expandedItems.forEach)
		{
			props.expandedItems.forEach(item =>
			{
				onToggleExpandedStateCallback(item, true, false)
			});
			forceUpdate();
		}
	}, [props.expandedItems, props.expandItemsOnce, props.startExpanded]);

	// callbacks
	function onItemSelected(itemIndex, modifierKeys)
	{
		if (!indexToProps.current.has(itemIndex))
		{
			return;
		}

		const target = indexToProps.current.get(itemIndex);

		if (!modifierKeys)
		{
			modifierKeys = {};
		}

		// start with the current selection
		const selectedItems = new Set(selectedNodeIds);
		let deselectedItems = new Set();

		if (selectedNodeIds.size > 0 && !selectedNodeIds.has(target.nodeId))
		{
			stopRenaming();
		}

		if (target === null || target === undefined)
		{
			console.warn("list item not found! index = " + itemIndex);
		}
		else
		{
			// single selection
			if (!isMultiSelectEnabled)
			{
				// clear previous selection
				deselectedItems = new Set(selectedItems);
				selectedItems.clear();

				if (modifierKeys.ctrlKey)
				{
					toggleSelection(target.nodeId, deselectedItems, selectedItems);
				}
				else
				{
					addToSelection(deselectedItems, target.nodeId, selectedItems);
				}
			}
			// multi-selection
			else
			{
				// handle shift - select range from last to current
				if (modifierKeys.shiftKey && lastSelectedIndex.current >= 0)
				{
					// select the item itself (always)
					addToSelection(deselectedItems, target.nodeId, selectedItems);

					// clear last range selection from current selection
					lastShiftSelectedRange.current.clear();

					// select all items from the last selected item to the currently selected item
					if (lastSelectedIndex.current >= 0)
					{
						for (let i = itemIndex; i !== lastSelectedIndex.current; lastSelectedIndex.current > itemIndex ? i++ : i--)
						{
							const itemId = indexToProps.current.get(i)?.nodeId;
							if(!itemId) continue;

							addToSelection(deselectedItems, itemId, selectedItems);
							lastShiftSelectedRange.current.add(itemId);
						}
					}
					else
					{
						lastSelectedIndex.current = itemIndex;
					}
				}
				else if (modifierKeys.ctrlKey)
				{
					toggleSelection(target.nodeId, deselectedItems, selectedItems);

					// cache last selected item (active item)
					if (itemIndex !== lastSelectedIndex)
					{
						lastSelectedIndex.current = itemIndex;
					}
				}
				else
				{
					deselectedItems = new Set(selectedItems);
					selectedItems.clear();
					addToSelection(deselectedItems, target.nodeId, selectedItems);

					// cache last selected item (active item)
					if (itemIndex !== lastSelectedIndex.current)
					{
						lastSelectedIndex.current = itemIndex;
					}
				}
			}

			// handle ctrl - toggle selection
			if (modifierKeys.ctrlKey)
			{
				toggleSelection(target.nodeId, deselectedItems, selectedItems);
			}

			lastSelectedIndex.current = itemIndex;
		}

		// update whoever cares about it
		if (props.selectionChangedCallback)
		{
			const selectedProps = new Set();
			selectedItems.forEach(nodeId =>
			{
				const treeNode = idToTreeNode.current.get(nodeId);
				if (treeNode)
					selectedProps.add(treeNode.itemProps);
			});

			const deselectedProps = new Set();
			deselectedItems.forEach(nodeId =>
			{
				const treeNode = idToTreeNode.current.get(nodeId);
				if (treeNode)
					deselectedProps.add(treeNode.itemProps);
			});

			props.selectionChangedCallback(selectedProps, deselectedProps);
		}
	}

	function addToSelection(deselectedItems, nodeId, selectedItems)
	{
		deselectedItems.delete(nodeId);
		selectedItems.add(nodeId);
	}

	function nodeIdToIndex(nodeId)
	{
		return idToIndex.current.get(nodeId);
	}

	function toggleSelection(nodeId, deselectedItems, selectedItems)
	{
		// target was already selected; de-select it
		if (selectedNodeIds.has(nodeId))
		{
			deselectedItems.add(nodeId);
			if (nodeIdToIndex(nodeId) === lastSelectedIndex.current)
			{
				lastSelectedIndex.current = -1;
			}
		}
		// target was not selected; select it
		else
		{
			addToSelection(deselectedItems, nodeId, selectedItems);
		}
	}

	// TODO TS Conversion: Explicitly type the return value
	function getRenamingItem(nodeId: string | null = null): { isRenaming: React.RefObject<boolean>, activateRename: () => void, clearRename: (ref: React.RefObject<HTMLDivElement>) => void } | null
	{
		const renamingItemID = nodeId ? nodeId : indexToProps.current.get(lastSelectedIndex.current)?.nodeId;
		let renamingItem = null;
		if (renamingItemID)
		{
			renamingItem = nodeIdToProps.current.get(renamingItemID)?.renameFunctions;
		}
		return renamingItem;
	}

	function stopRenaming()
	{
		const renamingItem = getRenamingItem();
		if (renamingItem)
		{
			if (renamingItem.isRenaming.current)
			{
				renamingItem.clearRename(rootRef);
				return true;
			}
		}
		return false;
	}

	function clearSelection(e)
	{
		if (e.shiftKey)
			return;

		if (stopRenaming())
			return;

		if (props.readOnly)
			return;

		if (props.selectionChangedCallback)
		{
			const selectedProps = new Set();
			selectedNodeIds.forEach(nodeId =>
			{
				const treeNode = idToTreeNode.current.get(nodeId);
				if (treeNode)
					selectedProps.add(treeNode.itemProps)
			});

			props.selectionChangedCallback(new Set(), new Set(selectedProps));
		}

		lastSelectedIndex.current = -1;
		lastShiftSelectedRange.current.clear();
	}

	function checkObjectSelected(nodeId)
	{
		return selectedNodeIds.has(nodeId);
	}

	function checkItemExpanded(nodeId)
	{
		return expandedItems.current.has(nodeId);
	}

	function onToggleExpandedStateCallback(nodeId, shouldExpand, isRecursive)
	{
		if(!nodeId && nodeId !== 0) return;

		if (shouldExpand)
		{
			expandedItems.current.add(nodeId);
			nodeIdToProps.current.get(nodeId).setIsExpanded();

			if (isRecursive)
			{
				idToTreeNode.current.get(nodeId).traverse((childNode) =>
				{
					expandedItems.current.add(childNode.id);
				})
			}
		}
		else
		{
			expandedItems.current.delete(nodeId);
			nodeIdToProps.current.get(nodeId).setIsExpanded();

			if (isRecursive)
			{
				idToTreeNode.current.get(nodeId).traverse((childNode) =>
				{
					expandedItems.current.delete(childNode.id);
				})
			}

			setHasExpanded(true); //to prevent auto expand from not happening on multiple renders we flag it as done after user input
		}

		rebuildIndexToProps();
	}

	//bBeforeItem : true=before, false=after, undefined=drop on item itself
	function onListItemDroppedInternal(droppedItemProps, dropTargetItemProps, bBeforeItem)
	{
		const dropTargetItemId = dropTargetItemProps ? dropTargetItemProps.nodeId : "";
		let _dropCallback = props.itemDroppedCallback;

		if (undefined !== bBeforeItem)
		{
			_dropCallback = bBeforeItem ? props.itemDroppedBeforeCallback : props.itemDroppedAfterCallback;
		}

		if (!_dropCallback) return;

		//const droppedItemIds = droppedItemProps.map(p => p.nodeId);
		const droppedItemIds = Array.from(props.selectedItems || []);

		let lastSelectedIndexNodeId = -1;

		if (lastSelectedIndex.current >= 0)
		{
			if (undefined != indexToProps.current.get(lastSelectedIndex.current))
			{
				lastSelectedIndexNodeId = indexToProps.current.get(lastSelectedIndex.current).nodeId;
			}
			else
			{
				console.warn({
					desc: "lastSelectedIndex not found in index",
					payload: {
						lastSelectedIndex: lastSelectedIndex,
						indexToProps: indexToProps,
						droppedItemProps: droppedItemProps,
						dropTargetItemProps: dropTargetItemProps
					}
				})
			}
		}

		//Make sure the dropped item is in the selected items array... Add it to the dropped items if it isn't
		if (droppedItemProps.length)
		{
			if (!droppedItemIds.includes(droppedItemProps[0].nodeId))
			{
				if (!props.multiSelectEnabled ||
					((!Input.getModifier(ModifierKeyCodes.CTRL)) && (!Input.getModifier(ModifierKeyCodes.SHIFT)))
				)
				{
					//Replace selection with dropped item...
					droppedItemIds.length = 0;
				}
				droppedItemIds.push(droppedItemProps[0].nodeId);
				//Need to update lastSelectedIndex at the end of the drag
				lastSelectedIndexNodeId = droppedItemProps[0].nodeId;

				//Shift?
				//Sadly this only works in FF - Chromium doesn't trigger beginDrag when Shift is held down
				// https://github.com/react-dnd/react-dnd/issues/683
				// https://bugs.chromium.org/p/chromium/issues/detail?id=646339
				if (props.multiSelectEnabled && Input.getModifier(ModifierKeyCodes.SHIFT) && lastSelectedIndex.current >= 0)
				{
					//Add needed items to selection - select range from last to current
					// select all items from the last selected item to the currently selected item
					const itemIndex = nodeIdToIndex(droppedItemProps[0].nodeId);
					for (let i = itemIndex; i !== lastSelectedIndex.current; lastSelectedIndex.current > itemIndex ? i++ : i--)
					{
						const itemId = indexToProps.current.get(i).nodeId;
						if (itemId != dropTargetItemId)
						{
							droppedItemIds.push(itemId);
						}
					}
				}
			}
		}

		// filter parents of target
		const legalDropIds = new Set(droppedItemIds);
		droppedItemIds.forEach(id =>
		{
			const dropNode = idToTreeNode.current.get(id);
			if (dropNode)
			{
				dropNode.traverse((childNode) =>
				{
					if (childNode.id === dropTargetItemId)
					{
						legalDropIds.delete(id);
					}
				});
			}
		});

		const legalItemsProps = new Set();
		legalDropIds.forEach(nodeId =>
		{
			const node = idToTreeNode.current.get(nodeId);
			if (node)
			{
				legalItemsProps.add(node.itemProps);
			}
		});

		const targetNode = idToTreeNode.current.get(dropTargetItemId);
		_dropCallback(legalItemsProps, (targetNode ? targetNode.itemProps : null));

		//Update last selected index (note: after the drag, the node may not always be visible, therefore not always indexed)
		lastSelectedIndex.current = nodeIdToIndex(lastSelectedIndexNodeId) ? nodeIdToIndex(lastSelectedIndexNodeId) : -1;
	}

	function deleteSelectedItems()
	{
		const deleted = new Set();

		selectedNodeIds.forEach(nodeId =>
		{
			if (nodeId)
			{
				if (!idToTreeNode.current.get(nodeId).itemProps.protected)
				{
					deleted.add(idToTreeNode.current.get(nodeId).itemProps);
				}
			}
		});

		if (props.itemDeleteCallback)
		{
			props.itemDeleteCallback(deleted);
		}
	}

	function traverseSelection(distance)
	{
		const indexToSelect = MathUtils.wrapNumber(lastSelectedIndex.current + distance, 0, listIndex.current.index - 1);
		const warp = Math.abs(indexToSelect - lastSelectedIndex.current) != 1 ? true : false;
		const key = distance > 0 ? KeyCodes.DOWN_ARROW : KeyCodes.UP_ARROW;
		scrollToSelectedItem(true, key, warp);
		onItemSelected(indexToSelect, null);
	}

	function handleKeyDown(e)
	{
		if (props.readOnly)
			return;

		if (lastSelectedIndex.current < 0 && selectedNodeIds.size > 0)
		{
			const selection = selectedNodeIds.values().next().value;
			indexToProps.current.forEach((value, key) =>
			{
				if (value.nodeId === selection)
				{
					lastSelectedIndex.current = key;
				}
			});
		}

		const renamingItem = getRenamingItem();

		switch (e.keyCode)
		{
			case KeyCodes.F2:
				if (renamingItem && !props.blockRename)
				{
					if (renamingItem.activateRename)
					{
						renamingItem.activateRename();
					}
				}
				break;
			case KeyCodes.ESCAPE:
				clearSelection(e);
				break;
			case KeyCodes.DELETE:
				if (renamingItem && renamingItem.isRenaming.current)
				{
					return;
				}
				e.preventDefault();
				e.stopPropagation();
				deleteSelectedItems();
				break;
			// expand or collapse selected nodes
			case KeyCodes.LEFT_ARROW:
			case KeyCodes.RIGHT_ARROW:
				if (renamingItem && renamingItem.isRenaming.current) return;
				e.preventDefault();
				selectedNodeIds.forEach(selectedNodeId =>
				{
					onToggleExpandedStateCallback(selectedNodeId, e.keyCode === KeyCodes.RIGHT_ARROW, e.altKey);
				});
				forceUpdate();
				break;
			// from last selected index, move up or down
			case KeyCodes.UP_ARROW:
			case KeyCodes.DOWN_ARROW:
				e.preventDefault();
				traverseSelection(e.keyCode === KeyCodes.DOWN_ARROW ? 1 : -1);
				break;
			default:

				break;
		}
	}

	function buildNodeHierarchy(parentNode, reactElement)
	{
		const arChildren = getChildrenArray(reactElement.props.children);

		// React.Children.map(reactElement.props.children,
		arChildren.map((reactChild) =>
		{
			if (React.isValidElement(reactChild))
			{
				const childNode = new TreeNode(reactChild.props as ITreeNodeProps, parentNode);
				childNode.indentLevel = parentNode.indentLevel + 1;
				idToTreeNode.current.set(childNode.id, childNode);
				parentNode.addChild(childNode);
				buildNodeHierarchy(childNode, reactChild);
			}
		});
	}

	function nodeSort(a, b)
	{
		if (!(a.props && b.props))
		{
			console.warn("Sort function called for items without props...");
			return 0;
		}

		//Pinned items
		if (a.props.pinnedItemIndex)
		{
			if (b.props.pinnedItemIndex)
			{
				return (a.props.pinnedItemIndex > b.props.pinnedItemIndex ? 1 : -1);
			}
			return -1;
		}
		if (b.props.pinnedItemIndex)
		{
			return 1;
		}

		//Regular A-Z/Z-A sorting
		const sLabel1 = a.props.label && a.props.label.localeCompare ? a.props.label : null;
		const sLabel2 = b.props.label && b.props.label.localeCompare ? b.props.label : null;

		if (!(sLabel1 && sLabel2))
		{
			console.warn("Sort function called for items without text labels...");
			return 0;
		}

		return sLabel1.localeCompare(sLabel2) * sortDirMultiplier;
	}


	function getChildrenArray(children?: React.ReactElement[])
	{
		const arChildren = (children ? React.Children.toArray(children) : []) as React.ReactElement[];
		if (sortOrder !== SortOrders.None)
		{
			arChildren.sort(nodeSort);
		}
		
		return arChildren;
	}

	function buildNodeMap()
	{
		const tree = new Array<TreeNode>();

		const arChildren = getChildrenArray(props.children);

		arChildren.map((child) =>
		{
			if (React.isValidElement(child))
			{
				// TODO: fix type
				const node = new TreeNode((child as any).props);
				idToTreeNode.current.set(node.id, node);
				tree.push(node);
				buildNodeHierarchy(node, child);
			}
		});

		return tree;
	}

	function getIndentLevelForItem(nodeId)
	{
		return idToTreeNode.current.get(nodeId).indentLevel;
	}

	function expandSelectedItems()
	{
		// get treenode from each id
		selectedNodeIds.forEach(id =>
		{
			// expand upwards until root
			let node = idToTreeNode.current.get(id);
			while (node)
			{
				if (node.id !== id)
				{
					const nodeProps = nodeIdToProps.current.get(node.id);
					if (nodeProps) nodeProps.setIsExpanded(true); //forcing the required nodes to be expanded instantly and not wait for oListDecorators to wake up
					expandedItems.current.add(node.id);
				}

				node = node.parentNode;
			}
		});
	}

	function scrollToSelectedItem(forceScroll = false, keyCode?: number, wrap = false)
	{
		if (!rootRef.current || (!autoScroll && !forceScroll))
			return;

		// scroll to last selected node, because why not
		let scrollTarget = rootRef.current.scrollTop;

		if (selectedNodeIds.size > 0)
		{
			//const nodeProps = nodeIdToProps.current.get(selectedNodeIds.values().next().value);
			const nodeProps = nodeIdToProps.current.get(Array.from(selectedNodeIds).pop()); // scroll to last (newest) item in accordance with WC20-2766
			if (nodeProps)
			{
				expandSelectedItems();

				if (nodeProps.htmlElementRef)
				{
					const topScrollMargin = props.topScrollMargin ? props.topScrollMargin : 50;
					const bottomScrollMargin = props.bottomScrollMargin ? props.bottomScrollMargin : 20;

					if (!keyCode)
					{
						if (nodeProps.htmlElementRef.offsetTop > scrollTarget + rootRef.current.clientHeight - bottomScrollMargin)
						{
							scrollTarget = nodeProps.htmlElementRef.offsetTop - rootRef.current.clientHeight + bottomScrollMargin;
						}
						else if (nodeProps.htmlElementRef.offsetTop - topScrollMargin < scrollTarget)
						{
							scrollTarget = nodeProps.htmlElementRef.offsetTop - topScrollMargin;
						}
					}
					else if (!wrap)
					{
						if (nodeProps.htmlElementRef.offsetTop > scrollTarget + rootRef.current.clientHeight - bottomScrollMargin && keyCode === KeyCodes.DOWN_ARROW)
						{
							scrollTarget = nodeProps.htmlElementRef.offsetTop - rootRef.current.clientHeight + bottomScrollMargin;
						}
						else if (nodeProps.htmlElementRef.offsetTop - topScrollMargin < scrollTarget && keyCode === KeyCodes.UP_ARROW)
						{
							scrollTarget = nodeProps.htmlElementRef.offsetTop - topScrollMargin;
						}
					}
					else
					{
						if (keyCode === KeyCodes.DOWN_ARROW)
						{
							scrollTarget = 0;
						}
						else if (keyCode === KeyCodes.UP_ARROW)
						{
							scrollTarget = 50000;
						}
					}
				}

				if (props.autoRename && nodeProps.renameFunctions?.activateRename && !props.blockRename) nodeProps.renameFunctions.activateRename();
			}
			else
			{
				console.warn("Invalid selected node ID", selectedNodeIds);
			}
		}

		rootRef.current.scrollTop = scrollTarget;
	}

	function getLastSetValue(set)
	{
		let value;
		for (value of set);
		return value;
	}

	function getDraggedObjects(): Array<{ index: number, props: any }>
	{
		const dragged = new Array<{ index: number, props: any }>();
		if (!props.multiSelectEnabled)
			return dragged;

		indexToProps.current.forEach((value, key) =>
		{
			if (selectedNodeIds.has(value.nodeId) && !dragged.find(node => node.props.nodeId === value.nodeId))
			{
				// make sure the node can be dragged!
				if ((value.dndOverrides && value.dndOverrides["dragEnabled"] !== undefined && value.dndOverrides["dragEnabled"]) ||
					(value.dnd && value.dnd.dragEnabled))
				{
					dragged.push({
						index: key,
						props: value
					})
				}
			}
		});

		return dragged;
	}

	function rebuildItemIndexToProps(item)
	{
		const arChildren = getChildrenArray(item.props.children);

		arChildren.map((child) =>
		{
			if (React.isValidElement(child))
			{
				indexToProps.current.set(listIndex.current.index, child.props);
				// TODO: fix type
				idToIndex.current.set((child.props as any).nodeId, listIndex.current.index);
				listIndex.current.index++;

				// TODO: fix type
				if (expandedItems.current.has((child.props as any).nodeId))
					rebuildItemIndexToProps(child);
			}
		});
	}

	function rebuildIndexToProps()
	{
		listIndex.current.index = 0;
		indexToProps.current.clear();
		idToIndex.current.clear();

		const arChildren = getChildrenArray(props.children);
		arChildren.map((child) =>
		{
			if (React.isValidElement(child))
			{
				indexToProps.current.set(listIndex.current.index, child.props);
				// TODO: fix type
				idToIndex.current.set((child.props as any).nodeId, listIndex.current.index);
				listIndex.current.index++;

				// TODO: fix type
				if (expandedItems.current.has((child.props as any).nodeId))
					rebuildItemIndexToProps(child);
			}
		});
	}

	function activateRename()
	{
		const renamingItem = indexToProps.current.get(lastSelectedIndex.current)?.ref?.current;
		if (renamingItem && !props.blockRename)
		{
			if (!renamingItem.isRenaming && renamingItem.activateRename)
			{
				renamingItem.activateRename();
			}
		}
	}

	function registerItemRefInList(nodeId, refObj)
	{
		nodeIdToProps.current.set(nodeId, refObj);
	}

	function decorateProps()
	{
		return {
			getIndex: nodeIdToIndex,
			getChildrenArray: getChildrenArray,
			registerItemRefInList: registerItemRefInList,
			activateRename: activateRename,
			selectionQuery: checkObjectSelected,
			onSelectCallback: onItemSelected,
			expansionQuery: checkItemExpanded,
			onToggleExpandedStateCallback: onToggleExpandedStateCallback,
			onDroppedCallback: manageItemDrop,
			dnd: dndSettings,
			getDraggedObjects: getDraggedObjects,
			propsDecorator: decorateProps,
			contextCallback: props.contextAction,
			useAltColor: props.useAltColor,
			doubleClickCallback: props.doubleClickCallback,
			autoRename: props.autoRename,
			getIndentLevel: getIndentLevelForItem,
			scrollToSelectedItem: scrollToSelectedItem,
			autoScroll: props.autoScroll,
		};
	}

	// render
	return (
		drop(
			<div className={["listViewContainer", props.className].join(" ")}
				style={props.style}
				data-name={props["data-name"] ?? "list-view-root"}
				ref={rootRef}
				onClick={clearSelection}
				tabIndex={0}
				onKeyDownCapture={handleKeyDown}
				data-hover={hovered && canDrop ? "true" : "false"}
				onContextMenu={props.onContextMenu}
			>
				{
					props.children ?
						props.parentDecoratorsMapID ?
							getChildrenArray(props.children) :
							getChildrenArray(props.children).map((child) =>
							{
								if (React.isValidElement(child))
								{
									return React.cloneElement(child, {
										parentIndex: -1,
										...decorateProps(),
										...(child.props as any)["tooltip"],
									});
								}
							})
						: null
				}
			</div>
		)
	);
});